import * as React from "react";
import { useInfiniteQuery, useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Download, Loader2 } from "lucide-react";
import { api, saveBlob } from "@/lib/api";
import type { RegistryInvoicesResponse } from "@/types/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { formatMoney } from "@/lib/billing-format";
import {
  EMPTY_FILTERS,
  InvoiceFilters,
  filtersToParams,
  type InvoiceFilterState,
} from "./invoice-filters";
import { InvoicesTable } from "./invoices-table";
import { StatCard } from "./stat-card";

const PAGE_SIZE = 50;

/** Debounce a value so typing in the instance field doesn't fire a query per key. */
function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = React.useState(value);
  React.useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), ms);
    return () => window.clearTimeout(id);
  }, [value, ms]);
  return debounced;
}

export function InvoicesTab() {
  const { t } = useTranslation("billing");
  const [filters, setFilters] = React.useState<InvoiceFilterState>(EMPTY_FILTERS);
  const debounced = useDebounced(filters, 300);
  const query = filtersToParams(debounced).toString();

  const { data, isLoading, error, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ["billing", "registry", "invoices", query],
      queryFn: ({ pageParam }) => {
        const params = new URLSearchParams(query);
        params.set("limit", String(PAGE_SIZE));
        if (pageParam) params.set("cursor", pageParam);
        return api.get<RegistryInvoicesResponse>(
          `/api/admin/billing/invoices?${params.toString()}`
        );
      },
      initialPageParam: null as string | null,
      getNextPageParam: (lastPage) => lastPage.nextCursor,
    });

  const exportCsv = useMutation({
    mutationFn: async () => {
      const blob = await api.getBlob(`/api/admin/billing/invoices.csv?${query}`);
      saveBlob(blob, `invoices-${new Date().toISOString().slice(0, 10)}.csv`);
    },
    onError: (err: unknown) => {
      toast.error(
        t("payments.exportFailed", { message: err instanceof Error ? err.message : String(err) })
      );
    },
  });

  const items = data?.pages.flatMap((p) => p.items) ?? [];
  const totals = data?.pages[0]?.totals;

  return (
    <div className="space-y-4">
      <InvoiceFilters value={filters} onChange={setFilters} />

      <div className="flex justify-end">
        <Button
          variant="outline"
          size="sm"
          onClick={() => exportCsv.mutate()}
          disabled={exportCsv.isPending}
        >
          {exportCsv.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Download className="h-4 w-4" />
          )}
          {t("payments.exportCsv")}
        </Button>
      </div>

      {isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : error ? (
        <p className="text-sm text-destructive">
          {t("loadFailed", { message: error instanceof Error ? error.message : String(error) })}
        </p>
      ) : (
        <>
          {totals ? (
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              <StatCard
                title={t("payments.totals.paid")}
                value={formatMoney(totals.paidCents, "EUR")}
                hint={t("payments.totals.paidHint")}
                tone="success"
              />
              <StatCard
                title={t("payments.totals.refunded")}
                value={formatMoney(totals.refundedCents, "EUR")}
                hint={t("payments.totals.refundedHint")}
              />
              <StatCard
                title={t("payments.totals.open")}
                value={formatMoney(totals.openCents, "EUR")}
                hint={t("payments.totals.hint")}
              />
              <StatCard
                title={t("payments.totals.overdue")}
                value={formatMoney(totals.overdueCents, "EUR")}
                hint={t("payments.totals.hint")}
                tone={totals.overdueCents > 0 ? "destructive" : "default"}
              />
            </div>
          ) : null}

          <Card>
            <CardContent className="pt-6">
              <InvoicesTable items={items} />
              {hasNextPage ? (
                <div className="mt-4 flex justify-center">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => void fetchNextPage()}
                    disabled={isFetchingNextPage}
                  >
                    {isFetchingNextPage ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                    {t("payments.loadMore")}
                  </Button>
                </div>
              ) : null}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
